import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Toast, { useToast } from '../components/Toast';
import SEO from '../components/SEO';

const Signup: React.FC = () => {
    const navigate = useNavigate();
    const { signUp, signInWithGoogle, authLoading } = useAuth();
    const { toast, showToast, hideToast } = useToast();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [username, setUsername] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (password.length < 6) {
            showToast('Password must be at least 6 characters', 'error');
            return;
        }
        const result = await signUp(email, password, username) as { success: boolean, message?: string, error?: string, session?: any, isExistingUser?: boolean };
        if (result.success) {
            showToast(result.message || 'Account created! Check your email to verify.', 'success');
            // Only navigate if we actually have a session (auto-login enabled in Supabase)
            if (result.session) {
                setTimeout(() => navigate('/dashboard'), 1500);
            }
        } else {
            if (result.isExistingUser) {
                showToast('Email already registered. Redirecting to login...', 'info');
                setTimeout(() => navigate('/login'), 2000);
            } else {
                showToast(result.error || 'Sign up failed', 'error');
            }
        }
    };

    const handleGoogle = async () => {
        const result = await signInWithGoogle();
        if (!result.success) {
            showToast(result.error || 'Google sign in failed', 'error');
        }
    };

    return (
        <div className="min-h-screen bg-dark-950 flex flex-col items-center justify-center px-4 pt-32 pb-12">
            <SEO
                title="Sign Up"
                description="Join Share Visuals for free and start creating stunning visuals for your SaaS journey."
            />
            {/* Background gradient blobs */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-primary-600/10 rounded-full blur-3xl" />
                <div className="absolute top-1/4 right-1/4 w-[300px] h-[300px] bg-secondary-500/8 rounded-full blur-3xl" />
            </div>

            <div className="relative w-full max-w-md animate-fade-in">
                <div className="text-center mb-10">
                    <h1 className="text-3xl font-bold text-white tracking-tight">Create your account</h1>
                    <p className="mt-2 text-dark-400 text-base font-medium">Free forever · 10 images/day · No credit card needed</p>
                </div>

                <div className="card-glass">
                    {/* Google OAuth */}
                    <button
                        id="google-signup-btn"
                        onClick={handleGoogle}
                        disabled={authLoading}
                        className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl border border-dark-600 text-dark-200 hover:text-white hover:border-dark-400 hover:bg-white/5 transition-all text-sm font-bold disabled:opacity-50"
                    >
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        Continue with Google
                    </button>

                    <div className="relative my-8">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-white/10" />
                        </div>
                        <div className="relative flex justify-center text-[10px] font-black uppercase tracking-widest text-dark-500">
                            <span className="bg-[#111827] px-4 rounded-full py-0.5">or sign up with email</span>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="signup-username" className="label">Username</label>
                            <input
                                id="signup-username"
                                type="text"
                                required
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="yourname"
                                className="input"
                            />
                        </div>
                        <div>
                            <label htmlFor="signup-email" className="label">Email address</label>
                            <input
                                id="signup-email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                className="input"
                            />
                        </div>
                        <div>
                            <label htmlFor="signup-password" className="label">Password</label>
                            <input
                                id="signup-password"
                                type="password"
                                autoComplete="new-password"
                                required
                                minLength={6}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Minimum 6 characters"
                                className="input"
                            />
                        </div>

                        <button
                            id="signup-submit-btn"
                            type="submit"
                            disabled={authLoading}
                            className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {authLoading && (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            )}
                            Create Account
                        </button>
                    </form>

                    <p className="mt-6 text-center text-sm text-dark-400">
                        Already have an account?{' '}
                        <Link to="/login" className="text-primary-400 hover:text-primary-300 font-medium">
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>

            {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
        </div>
    );
};

export default Signup;
